import React, { useEffect, useState, useRef } from 'react';
import { Card, Tag, Select, Button, Space, Typography, Empty, Switch } from 'antd';
import { ClearOutlined, DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { Socket } from 'socket.io-client';
import type { LogEntryPayload } from '../../../../../types/socket.types';
import type { BotLogs } from '../types/bot.types';

const { Text } = Typography;
const { Option } = Select;

export interface BotLogViewerComponentProps {
  botId: string;
  socket: Socket | null;
  initialLogs?: BotLogs[];
}

interface LogEntry extends LogEntryPayload {
  id: string;
}

// Helper function to convert BotLogs to LogEntry
const convertBotLogsToLogEntry = (botLog: BotLogs): LogEntry => ({
  ...botLog,
  id: `${botLog.timestamp}-${Math.random()}`,
});

const BotLogViewerComponent = ({ botId, socket, initialLogs }: BotLogViewerComponentProps) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<LogEntry[]>(() => {
    // Initialize with initialLogs if provided
    return initialLogs ? initialLogs.map(convertBotLogsToLogEntry) : [];
  });
  const [filterLevel, setFilterLevel] = useState<
    'all' | 'info' | 'success' | 'warning' | 'error' | 'debug'
  >('all');
  const logsEndRef = useRef<HTMLDivElement>(null);
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState<boolean>(true);
  const userScrolledUpRef = useRef<boolean>(false);

  // Check if user is at bottom of scroll
  const checkIfAtBottom = (): boolean => {
    if (!logsContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current;
    // Consider "at bottom" if within 50px of bottom
    return scrollHeight - scrollTop - clientHeight < 50;
  };

  // Handle scroll events to detect manual scrolling
  useEffect(() => {
    const container = logsContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const atBottom = checkIfAtBottom();
      if (atBottom) {
        // User scrolled back to bottom, re-enable auto-scroll
        userScrolledUpRef.current = false;
        if (!autoScroll) {
          setAutoScroll(true);
        }
      } else {
        // User scrolled up, disable auto-scroll
        userScrolledUpRef.current = true;
        if (autoScroll) {
          setAutoScroll(false);
        }
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [autoScroll]);

  // Auto-scroll to bottom when new logs arrive (only if auto-scroll is enabled and user hasn't scrolled up)
  useEffect(() => {
    if (autoScroll && !userScrolledUpRef.current && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  // Handle initialLogs prop changes
  useEffect(() => {
    if (initialLogs && initialLogs.length > 0) {
      // Convert initialLogs to LogEntry format
      const initialLogEntries = initialLogs.map(convertBotLogsToLogEntry);

      // Merge with existing logs, avoiding duplicates based on timestamp
      setLogs((prevLogs) => {
        // If we have no existing logs, just set the initial logs
        if (prevLogs.length === 0) {
          return initialLogEntries;
        }

        // Otherwise, merge initial logs with existing logs, avoiding duplicates
        const existingTimestamps = new Set(prevLogs.map((log) => log.timestamp));
        const newInitialLogs = initialLogEntries.filter(
          (log) => !existingTimestamps.has(log.timestamp)
        );

        // Combine: initial logs first (sorted by timestamp), then existing logs
        const combined = [...newInitialLogs, ...prevLogs];
        // Sort by timestamp to maintain chronological order
        return combined.sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
      });
    }
  }, [initialLogs]);

  // Listen to socket events
  useEffect(() => {
    if (!socket) return;

    const handleBotLog = (payload: LogEntryPayload) => {
      const logEntry: LogEntry = {
        ...payload,
        id: `${payload.timestamp}-${Math.random()}`,
      };
      setLogs((prev) => [...prev, logEntry]);
    };

    socket.on('bot:log', handleBotLog);

    return () => {
      socket.off('bot:log', handleBotLog);
    };
  }, [socket]);

  // Filter logs by level
  const filteredLogs = logs.filter((log) => {
    if (filterLevel === 'all') return true;
    return log.level === filterLevel;
  });

  // Get log level color
  const getLogLevelColor = (level: LogEntryPayload['level']): string => {
    switch (level) {
      case 'success':
        return 'green';
      case 'warning':
        return 'orange';
      case 'error':
        return 'red';
      case 'debug':
        return 'blue';
      case 'info':
      default:
        return 'default';
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch {
      return timestamp;
    }
  };

  // Clear logs
  const handleClearLogs = () => {
    setLogs([]);
    userScrolledUpRef.current = false;
    setAutoScroll(true);
  };

  // Scroll to bottom manually
  const handleScrollToBottom = () => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
      userScrolledUpRef.current = false;
      setAutoScroll(true);
    }
  };

  return (
    <Card>
      <div
        style={{
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text strong style={{ fontSize: '16px' }}>
          {t('bots.logs.title') || 'Bot Logs'}
        </Text>
        <Space>
          <Select value={filterLevel} onChange={setFilterLevel} style={{ width: 120 }} size="small">
            <Option value="all">{t('bots.logs.filter.all') || 'All'}</Option>
            <Option value="info">{t('bots.logs.filter.info') || 'Info'}</Option>
            <Option value="success">{t('bots.logs.filter.success') || 'Success'}</Option>
            <Option value="warning">{t('bots.logs.filter.warning') || 'Warning'}</Option>
            <Option value="error">{t('bots.logs.filter.error') || 'Error'}</Option>
            <Option value="debug">{t('bots.logs.filter.debug') || 'Debug'}</Option>
          </Select>
          <Space size="small">
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('bots.logs.autoScroll') || 'Auto-scroll'}
            </Text>
            <Switch size="small" checked={autoScroll} onChange={setAutoScroll} />
          </Space>
          {!autoScroll && (
            <Button
              icon={<DownOutlined />}
              size="small"
              onClick={handleScrollToBottom}
              title={t('bots.logs.scrollToBottom') || 'Scroll to bottom'}
            />
          )}
          <Button
            icon={<ClearOutlined />}
            size="small"
            onClick={handleClearLogs}
            disabled={logs.length === 0}
          >
            {t('bots.logs.clear') || 'Clear'}
          </Button>
        </Space>
      </div>
      <div
        ref={logsContainerRef}
        style={{
          maxHeight: '400px',
          overflowY: 'auto',
          border: '1px solid #f0f0f0',
          borderRadius: '4px',
          padding: '12px',
          backgroundColor: '#fafafa',
        }}
      >
        {filteredLogs.length === 0 ? (
          <Empty
            description={t('bots.logs.empty') || 'No logs available'}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {filteredLogs.map((log) => (
              <div
                key={log.id}
                style={{
                  marginBottom: '8px',
                  padding: '4px 0',
                  borderBottom: '1px solid #e8e8e8',
                }}
              >
                <Space size="small" wrap>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {formatTimestamp(log.timestamp)}
                  </Text>
                  <Tag color={getLogLevelColor(log.level)} style={{ margin: 0 }}>
                    {log.level.toUpperCase()}
                  </Tag>
                  <Text>{log.message}</Text>
                  {log.metadata && Object.keys(log.metadata).length > 0 && (
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      {JSON.stringify(log.metadata)}
                    </Text>
                  )}
                </Space>
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
        )}
      </div>
    </Card>
  );
};

export default BotLogViewerComponent;
